import { useMemo } from 'react';
import { AlertCircle, Package } from 'lucide-react';
import { useTickets } from '@/hooks/useProduction';
import {
  STATIONS, STATION_LABELS,
  currentStation, type Station, type WorkTicket,
} from '@/types/production';
import { Card } from '@/components/ui/Card';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { TicketCard } from '@/components/production/TicketCard';

export function ShopFloorBoardPage() {
  const { data, isLoading, error } = useTickets({ limit: 500 });

  const byStation = useMemo(() => {
    const grouped = new Map<Station, WorkTicket[]>();
    STATIONS.forEach((s) => grouped.set(s, []));
    if (!data) return grouped;
    for (const t of data) {
      if (['draft', 'complete', 'cancelled'].includes(t.status)) continue;
      const s = currentStation(t);
      if (!s) continue;
      grouped.get(s)!.push(t);
    }
    // Sort each column: rush > high > normal > low; then by due date asc
    const priorityRank = { rush: 0, high: 1, normal: 2, low: 3 };
    grouped.forEach((tickets) => {
      tickets.sort((a, b) => {
        const dp = priorityRank[a.priority] - priorityRank[b.priority];
        if (dp !== 0) return dp;
        const da = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const db = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return da - db;
      });
    });
    return grouped;
  }, [data]);

  const stats = useMemo(() => {
    if (!data) return { active: 0, blocked: 0, rush: 0, late: 0 };
    const now = Date.now();
    const activeStatuses = ['queued', 'in_progress', 'blocked'];
    return {
      active: data.filter((t) => activeStatuses.includes(t.status)).length,
      blocked: data.filter((t) => t.status === 'blocked').length,
      rush: data.filter((t) => t.priority === 'rush' && activeStatuses.includes(t.status)).length,
      late: data.filter((t) =>
        activeStatuses.includes(t.status) && t.dueDate && new Date(t.dueDate).getTime() < now,
      ).length,
    };
  }, [data]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load shop floor"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  const total = data?.length ?? 0;
  if (total === 0) {
    return (
      <Card>
        <EmptyState
          icon={Package}
          title="No work tickets yet"
          description={`Create tickets manually or generate them from a scheduled order. Tickets will flow through ${STATIONS.length} stations.`}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Active"  value={String(stats.active)} />
        <Stat label="Blocked" value={String(stats.blocked)} alert={stats.blocked > 0} />
        <Stat label="Rush"    value={String(stats.rush)}    alert={stats.rush > 0} />
        <Stat label="Past Due" value={String(stats.late)}   alert={stats.late > 0} />
      </div>

      {/* The board — horizontal scroll on narrow screens */}
      <div className="overflow-x-auto -mx-8 px-8 pb-4">
        <div className="flex gap-3 min-w-max">
          {STATIONS.map((station) => {
            const tickets = byStation.get(station) ?? [];
            return (
              <div
                key={station}
                className="w-[17rem] flex-shrink-0 flex flex-col bg-bg-subtle border border-border rounded-sm"
              >
                <header className="px-4 py-3 border-b border-border bg-bg-panel flex items-baseline justify-between">
                  <div>
                    <div className="kicker">{STATION_LABELS[station]}</div>
                    <div className="text-2xs text-text-dim mt-0.5">
                      {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="font-display text-xl text-brass-300 tabular">
                    {tickets.length}
                  </div>
                </header>

                <div className="flex-1 p-2 space-y-2 min-h-[8rem]">
                  {tickets.length === 0 ? (
                    <div className="text-center py-8 text-2xs text-text-dim uppercase tracking-ultra">
                      Empty
                    </div>
                  ) : (
                    tickets.map((t) => <TicketCard key={t.id} ticket={t} compact />)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-2xl tabular ${alert ? 'text-warning' : 'text-text'}`}>
        {value}
      </div>
    </div>
  );
}
